<div class="bulk-redo-container flex column">
  <div class="header flex row space-between align-items-baseline">
    <h3>Bulk reschedule failed transcoding jobs</h3>
    <button
      type="button"
      class="dialog-close-btn"
      aria-label="Close"
      (click)="cancel()"
    >
      ×
    </button>
  </div>

  <div class="toolbar flex column">
    <div class="toolbar-row flex row wrap">
      <div class="toolbar-field">
        <span class="field-label">Search</span>
        <input
          class="filter-input"
          type="search"
          placeholder="Job name"
          [(ngModel)]="nameFilter"
          (ngModelChange)="onFiltersChanged()"
          [disabled]="isLoading || isApplying"
        />
      </div>
      <div class="toolbar-field">
        <span class="field-label">Failed after (UTC)</span>
        <div class="filter-input-row flex row align-items-center">
          <input
            class="filter-input"
            type="datetime-local"
            [(ngModel)]="failedAfterFilter"
            (ngModelChange)="onFiltersChanged()"
            [disabled]="isLoading || isApplying"
          />
          <button
            type="button"
            class="link-btn"
            *ngIf="failedAfterFilter"
            [disabled]="isLoading || isApplying"
            (click)="clearFailedAfterFilter()"
          >
            Clear
          </button>
        </div>
      </div>
    </div>

    <div class="toolbar-row flex row wrap align-items-end">
      <button
        type="button"
        class="link-btn"
        [disabled]="isLoading || isApplying || filteredRows.length === 0"
        (click)="toggleSelectAll()"
      >
        {{ allFilteredSelected ? 'Clear selection' : 'Select all visible' }}
      </button>
      <div class="range-group flex row align-items-center">
        <span class="field-label">Rows</span>
        <input
          class="range-input"
          type="number"
          min="1"
          [(ngModel)]="rangeFrom"
          [disabled]="isLoading || isApplying"
        />
        <span>to</span>
        <input
          class="range-input"
          type="number"
          min="1"
          [(ngModel)]="rangeTo"
          [disabled]="isLoading || isApplying"
        />
        <button
          type="button"
          class="link-btn"
          [disabled]="isLoading || isApplying || filteredRows.length === 0"
          (click)="applyRangeSelection()"
        >
          Apply range
        </button>
      </div>
    </div>

    <label class="reschedule-toggle" *ngIf="lastBulkAttemptCount > 0">
      <input
        type="checkbox"
        [checked]="limitToLastBulkAttempt"
        (change)="onLimitToLastBulkAttemptChanged($event)"
        [disabled]="isLoading || isApplying"
      />
      <span
        >Only last bulk attempt ({{ lastBulkAttemptCount }} job{{
          lastBulkAttemptCount === 1 ? '' : 's'
        }})</span
      >
    </label>
    <label class="reschedule-toggle">
      <input
        type="checkbox"
        [checked]="hideSuccessfullyRedone"
        (change)="onHideSuccessfullyRedoneChanged($event)"
        [disabled]="isLoading || isApplying"
      />
      <span>Hide jobs whose redo finished successfully (per media item)</span>
    </label>

    <p class="hint">
      Uses the same Jobs list data as History (no per-job JobDetails calls).
      Rows with the same profile, destination, and side effect are combined
      into one NewJob request, like manual media IDs in New Job.
    </p>
  </div>

  <div class="table-wrapper">
    <div *ngIf="isLoading" class="state-message">Loading failed jobs…</div>

    <table
      class="jobs-table"
      *ngIf="!isLoading && filteredRows.length > 0"
      [attr.data-filter-revision]="filterRevision"
    >
      <colgroup>
        <col class="col-check" />
        <col class="col-num" />
        <col class="col-name" />
        <col class="col-destination" />
        <col class="col-profile" />
        <col class="col-failed" />
        <col class="col-status" />
        <col class="col-side-effect" />
      </colgroup>
      <thead>
        <tr>
          <th class="col-check"></th>
          <th class="col-num">#</th>
          <th class="col-name">Name</th>
          <th class="col-destination">Destination</th>
          <th class="col-profile">Profile</th>
          <th class="col-failed">Failed (UTC)</th>
          <th class="col-status">Item status</th>
          <th class="col-side-effect">Side effect</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of filteredRows; let i = index">
          <td class="col-check">
            <input
              type="checkbox"
              [checked]="row.selected"
              [disabled]="isApplying"
              (change)="toggleRowSelection(row)"
            />
          </td>
          <td class="col-num">{{ i + 1 }}</td>
          <td class="col-name" [title]="row.name">{{ row.name }}</td>
          <td class="col-destination" [title]="row.destinationName">
            {{ row.destinationName }}
          </td>
          <td class="col-profile" [title]="row.profileName">
            {{ row.profileName }}
          </td>
          <td class="col-failed">{{ formatUtc(row.failedAt) }}</td>
          <td class="col-status">{{ row.itemStatusDisplay }}</td>
          <td class="col-side-effect" [title]="row.sideEffectName">
            {{ row.sideEffectName }}
          </td>
        </tr>
      </tbody>
    </table>

    <div
      *ngIf="!isLoading && filteredRows.length === 0"
      class="state-message"
    >
      <ng-container *ngIf="rows.length === 0; else noFilterMatch">
        No failed transcoding jobs found.
      </ng-container>
      <ng-template #noFilterMatch>
        <p>No jobs match the current filters.</p>
        <p class="filter-hint" *ngIf="failedAfterFilter">
          Clear <strong>Failed after</strong> or use an earlier UTC time — job
          end times in History are UTC (e.g. failures at 05:11 are before
          11:47 on the same day).
        </p>
        <p class="filter-hint" *ngIf="limitToLastBulkAttempt && lastBulkAttemptCount > 0">
          Uncheck <strong>Only last bulk attempt</strong> to see all failed jobs
          in History, not just your recent batch.
        </p>
        <p class="filter-hint" *ngIf="hideSuccessfullyRedone">
          Uncheck <strong>Hide jobs whose redo finished successfully</strong> to
          also show originals whose redo already completed.
        </p>
      </ng-template>
    </div>
  </div>

  <div class="footer flex row space-between align-items-center">
    <span class="selection-summary">
      {{ selectedCount }} selected · {{ filteredRows.length }} shown ·
      {{ rows.length }} failed total
      <ng-container *ngIf="lastBulkAttemptCount > 0">
        · {{ lastBulkAttemptCount }} in last attempt</ng-container
      >
    </span>
    <div class="footer-actions flex row">
      <evrideo-hover-button [disabled]="isApplying" (click)="cancel()">
        <span>Cancel</span>
      </evrideo-hover-button>
      <evrideo-hover-button
        [disabled]="isLoading || isApplying || selectedCount === 0"
        (click)="apply()"
      >
        <span>{{ isApplying ? 'Scheduling…' : 'Reschedule' }}</span>
      </evrideo-hover-button>
    </div>
  </div>
</div>
